// Jornaya (LeadiD) + TrustedForm consent-certification scripts.
//
// Both vendors watch the page and publish an identifier that must accompany
// every outbound lead. Neither is reliable to read from a single place, so
// this module is deliberately defensive:
//
//  - The certificate is scanned for everywhere TrustedForm is known to publish
//    it, including as a window value that may be a string, an input element,
//    or an object with a .value — and as a fallback, ANY hidden input on the
//    page holding a cert URL.
//  - Every value is validated. Only a real https://cert.trustedform.com/ URL
//    counts as a certificate, and only a UUID counts as a Jornaya token, so an
//    empty input or an unsubstituted "[trustedFormUrl]" macro can never be
//    sent as proof of consent.
//  - A background poll keeps looking after load and caches what it finds at
//    module scope, so a value captured on one route survives navigation.
//
// Kept in one place because three separate lead forms load these — the
// campaign id must never drift between them.

declare global {
  interface Window {
    myCallbackFunction?: (token: string) => void
    xxTrustedFormCertUrl?: any
    xxTrustedFormToken?: string
    xxTrustedFormPingUrl?: any
    LeadiD?: { token?: string }
  }
}

export const LEADID_CAMPAIGN_KEY = '4f92abea-9576-ff28-406d-190d2255539f'
const LEADID_ACCOUNT_KEY = 'F687F0C3-5166-FEF0-1329-1B967270DE02'

export const TRUSTED_FORM_CERT_FIELD = 'xxTrustedFormCertUrl'
export const TRUSTED_FORM_PING_FIELD = 'xxTrustedFormPingUrl'
export const LEADID_FIELD = 'leadid_token'

const LEADID_SCRIPT_ID = 'LeadiDscript_campaign'
const TRUSTED_FORM_SCRIPT_ID = 'TrustedFormScript'

let cachedTrustedFormUrl = ''
let cachedTrustedFormPingUrl = ''
let cachedLeadIdToken = ''

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isCertUrl(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.indexOf('https://cert.trustedform.com/') === 0
  )
}

function isPingUrl(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.indexOf('https://ping.trustedform.com/') === 0
  )
}

/** Jornaya tokens are UUIDs. Guards against reading back an empty input. */
function isLeadIdToken(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(
      value,
    )
  )
}

// A window value can be a string, an <input>, or an object with .value
// depending on SDK version. Unwrap all three shapes.
function unwrap(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof HTMLInputElement) return value.value
  if (typeof value === 'object' && value !== null && 'value' in value) {
    const inner = (value as { value: unknown }).value
    if (typeof inner === 'string') return inner
  }
  return ''
}

// ---------------------------------------------------------------------------
// Scanning
// ---------------------------------------------------------------------------

function scanForTrustedFormUrl(): string {
  // 1. Every input carrying the TrustedForm field name. TrustedForm appends
  //    its own input to each form it finds rather than reusing ours, so the
  //    first match is often our empty placeholder.
  const inputs = document.querySelectorAll(
    `input[name="${TRUSTED_FORM_CERT_FIELD}"]`,
  )
  for (let i = 0; i < inputs.length; i++) {
    const value = (inputs[i] as HTMLInputElement).value
    if (isCertUrl(value)) return value
  }
  // 2-4. The window values, in each of their possible shapes.
  for (const candidate of [
    window.xxTrustedFormCertUrl,
    window.xxTrustedFormToken,
    window.xxTrustedFormPingUrl,
  ]) {
    const value = unwrap(candidate)
    if (isCertUrl(value)) return value
  }
  // 5. Any hidden input holding a cert URL — catches SDK versions that write
  //    to a field name we didn't ask for.
  const hidden = document.querySelectorAll('input[type="hidden"]')
  for (let i = 0; i < hidden.length; i++) {
    const value = (hidden[i] as HTMLInputElement).value
    if (isCertUrl(value)) return value
  }
  // 6. The TrustedForm iframe's data attribute, on versions that expose it.
  const iframe = document.querySelector(
    'iframe[src*="trustedform"]',
  ) as HTMLIFrameElement | null
  if (iframe) {
    const attr = iframe.getAttribute('data-tf-cert-url')
    if (isCertUrl(attr)) return attr
  }
  return cachedTrustedFormUrl
}

function scanForTrustedFormPingUrl(): string {
  const inputs = document.querySelectorAll(
    `input[name="${TRUSTED_FORM_PING_FIELD}"]`,
  )
  for (let i = 0; i < inputs.length; i++) {
    const value = (inputs[i] as HTMLInputElement).value
    if (isPingUrl(value)) return value
  }
  const fromWindow = unwrap(window.xxTrustedFormPingUrl)
  if (isPingUrl(fromWindow)) return fromWindow
  const hidden = document.querySelectorAll('input[type="hidden"]')
  for (let i = 0; i < hidden.length; i++) {
    const value = (hidden[i] as HTMLInputElement).value
    if (isPingUrl(value)) return value
  }
  return cachedTrustedFormPingUrl
}

function scanForLeadIdToken(): string {
  const inputs = document.querySelectorAll(`input[name="${LEADID_FIELD}"]`)
  for (let i = 0; i < inputs.length; i++) {
    const value = (inputs[i] as HTMLInputElement).value
    if (isLeadIdToken(value)) return value
  }
  const fromWindow = window.LeadiD?.token
  if (isLeadIdToken(fromWindow)) return fromWindow
  return cachedLeadIdToken
}

function onCertFound(url: string) {
  if (cachedTrustedFormUrl === url) return
  cachedTrustedFormUrl = url
  console.log('[LeadTracking] TrustedForm cert URL captured:', url)
  // Mirror it back into the declared input so anything else reading the DOM
  // (including a later remount) sees it too.
  const field = document.getElementById(
    TRUSTED_FORM_CERT_FIELD,
  ) as HTMLInputElement | null
  if (field) field.value = url
}

function onLeadIdFound(token: string) {
  if (cachedLeadIdToken === token) return
  cachedLeadIdToken = token
  console.log('[LeadTracking] Jornaya LeadiD token captured:', token)
  const field = document.getElementById(
    LEADID_FIELD,
  ) as HTMLInputElement | null
  if (field) field.value = token
}

// ---------------------------------------------------------------------------
// Hidden inputs
// ---------------------------------------------------------------------------

function ensureHiddenInput(name: string, seed: string) {
  if (document.querySelector(`input[name="${name}"]`)) return
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = name
  input.id = name
  input.value = seed
  // Attached to a form when one exists — TrustedForm only certifies forms.
  const target = document.querySelector('form') || document.body
  target.appendChild(input)
}

// ---------------------------------------------------------------------------
// Injection
// ---------------------------------------------------------------------------

/**
 * Injects both certification scripts plus the Jornaya <noscript> fallback, and
 * starts the background poll.
 *
 * Idempotent, and the tags are never torn down: TrustedForm observes the
 * visitor's whole session to build the certificate, so removing and re-adding
 * the tag (React remounts, route changes) restarts that observation and can
 * leave the certificate empty at submit time.
 */
export function injectLeadCertificationScripts(): void {
  ensureHiddenInput(LEADID_FIELD, cachedLeadIdToken)
  ensureHiddenInput(TRUSTED_FORM_CERT_FIELD, cachedTrustedFormUrl)
  ensureHiddenInput(TRUSTED_FORM_PING_FIELD, cachedTrustedFormPingUrl)

  // Always (re)register the callback: the script tag survives client-side
  // route changes, but the handler reference must point at the live function.
  window.myCallbackFunction = (token: string) => {
    if (isLeadIdToken(token)) onLeadIdFound(token)
  }

  if (!document.getElementById(LEADID_SCRIPT_ID)) {
    const leadIdScript = document.createElement('script')
    leadIdScript.id = LEADID_SCRIPT_ID
    leadIdScript.type = 'text/javascript'
    leadIdScript.async = true
    leadIdScript.src =
      `//create.lidstatic.com/campaign/${LEADID_CAMPAIGN_KEY}.js` +
      '?snippet_version=2&callback=myCallbackFunction'
    leadIdScript.onerror = () =>
      console.error(
        '[LeadTracking] Jornaya LeadiD script FAILED to load (blocked or bad campaign id)',
      )
    document.body.appendChild(leadIdScript)

    if (!document.getElementById('LeadiDnoscript')) {
      const noscript = document.createElement('noscript')
      noscript.id = 'LeadiDnoscript'
      const img = document.createElement('img')
      img.src = `//create.leadid.com/noscript.gif?lac=${LEADID_ACCOUNT_KEY}&lck=${LEADID_CAMPAIGN_KEY}&snippet_version=2`
      img.alt = ''
      noscript.appendChild(img)
      document.body.appendChild(noscript)
    }
  }

  // TrustedForm's Certify SDK refuses to start if another copy is already
  // running ("Lock found ..."), and then no certificate is ever issued. A
  // second copy can come from anywhere — a tag manager, a partner snippet — so
  // check for ANY trustedform script, not just the one we add.
  const alreadyLoaded = document.querySelectorAll(
    'script[src*="trustedform"]',
  ).length
  if (!alreadyLoaded) {
    const provideReferrer = false
    const tf = document.createElement('script')
    tf.id = TRUSTED_FORM_SCRIPT_ID
    tf.type = 'text/javascript'
    tf.async = true
    // The vendor's published snippet, kept verbatim — including the protocol
    // switch and escape() — so it matches what TrustedForm's docs specify.
    tf.src =
      'http' +
      (document.location.protocol === 'https:' ? 's' : '') +
      '://api.trustedform.com/trustedform.js?provide_referrer=' +
      escape(String(provideReferrer)) +
      '&field=' +
      escape(TRUSTED_FORM_CERT_FIELD) +
      '&l=' +
      new Date().getTime() +
      Math.random()
    tf.onload = () => {
      console.log('[LeadTracking] TrustedForm script loaded successfully')
      // The SDK needs a beat after load before the certificate exists.
      window.setTimeout(() => {
        const url = scanForTrustedFormUrl()
        if (isCertUrl(url)) onCertFound(url)
      }, 1000)
    }
    tf.onerror = (err) =>
      console.error('[LeadTracking] TrustedForm script FAILED to load:', err)
    const first = document.getElementsByTagName('script')[0]
    if (first?.parentNode) first.parentNode.insertBefore(tf, first)
    else document.head.appendChild(tf)
  }

  startCertificationPoll()
}

// ---------------------------------------------------------------------------
// Background poll — covers a callback that fires before the handler is
// registered, and a script that writes straight to its input without calling
// back. Stops as soon as both vendors have reported.
// ---------------------------------------------------------------------------

let pollHandle: number | null = null

function startCertificationPoll() {
  if (pollHandle !== null) return
  let ticks = 0
  pollHandle = window.setInterval(() => {
    if (!cachedTrustedFormUrl) {
      const url = scanForTrustedFormUrl()
      if (isCertUrl(url)) onCertFound(url)
    }
    if (!cachedLeadIdToken) {
      const token = scanForLeadIdToken()
      if (isLeadIdToken(token)) onLeadIdFound(token)
    }
    if (!cachedTrustedFormPingUrl) {
      const ping = scanForTrustedFormPingUrl()
      if (isPingUrl(ping)) cachedTrustedFormPingUrl = ping
    }
    if (cachedTrustedFormUrl && cachedLeadIdToken) {
      window.clearInterval(pollHandle as number)
      pollHandle = null
      return
    }
    ticks++
    if (ticks === 120) {
      window.clearInterval(pollHandle as number)
      pollHandle = null
      console.warn('[LeadTracking] still missing after 60s:', {
        trustedFormCert: cachedTrustedFormUrl || '(none issued)',
        jornayaToken: cachedLeadIdToken || '(none issued)',
      })
    }
  }, 500)
}

export interface CertificationFields {
  leadIdToken: string
  trustedFormUrl: string
  trustedFormPingUrl: string
}

/** Current consent-certification values, or '' for any not yet issued. */
export function readCertificationFields(): CertificationFields {
  const trustedFormUrl = scanForTrustedFormUrl()
  if (isCertUrl(trustedFormUrl)) cachedTrustedFormUrl = trustedFormUrl
  const leadIdToken = scanForLeadIdToken()
  if (isLeadIdToken(leadIdToken)) cachedLeadIdToken = leadIdToken
  const ping = scanForTrustedFormPingUrl()
  if (isPingUrl(ping)) cachedTrustedFormPingUrl = ping
  return {
    leadIdToken: isLeadIdToken(leadIdToken) ? leadIdToken : '',
    trustedFormUrl: isCertUrl(trustedFormUrl) ? trustedFormUrl : '',
    trustedFormPingUrl: isPingUrl(ping) ? ping : '',
  }
}

/**
 * Resolves once TrustedForm has issued a certificate, or after `timeoutMs`.
 *
 * A visitor who arrives on a deep link with prefilled answers can reach the
 * contact step almost immediately, firing an outbound call before the cert
 * exists — which is how a request ends up with leadIdToken but no
 * trustedFormUrl. Awaiting this first closes that window; it never blocks for
 * longer than the timeout, so a blocked script only delays the call briefly.
 */
export function waitForTrustedFormCert(timeoutMs = 4000): Promise<string> {
  const existing = scanForTrustedFormUrl()
  if (isCertUrl(existing)) {
    onCertFound(existing)
    return Promise.resolve(existing)
  }
  return new Promise((resolve) => {
    const startedAt = Date.now()
    const poll = window.setInterval(() => {
      const value = scanForTrustedFormUrl()
      if (isCertUrl(value)) {
        window.clearInterval(poll)
        onCertFound(value)
        resolve(value)
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(poll)
        console.warn(
          '[LeadTracking] No TrustedForm certificate after ' +
            `${timeoutMs}ms — the lead will post without one.`,
        )
        resolve('')
      }
    }, 250)
  })
}
